/**
 * ControlSurfaceSectionPanel — the live 2D station preview for the Control
 * Surfaces workbench (ControlSurfacesSurface).
 *
 * Pure display: the parent runs the in-browser cove engine (computeCsShape,
 * debounced) and hands the resulting CsShapeResult down. This panel draws the
 * wing contour (primary token), the DEFLECTED control-surface contour (amber
 * token — the engine already rotates it about the hinge), and the hinge dot on
 * a <canvas>, fit-to-extents with padding. Under the canvas, slot-health chips
 * (gap / overlap / exit width / convergence) come from csSlotHealth, colored
 * ok - warn - bad with the same V5 palette convention as RiggingTable
 * (primary / amber / warn).
 *
 * A null shape (hinge outside the airfoil at this station) renders an honest
 * inline message instead of a crash.
 */
import { useEffect, useMemo, useRef, type CSSProperties } from "react";

import type { CsShapeResult } from "../highlift/geometry/csEngine";
import { csSlotHealth } from "../highlift/geometry/slottedSection";
import type { SlotMetrics } from "../highlift/geometry/gapMetrics";
import type { HighliftPalette } from "../highlift/foil/useHighliftTheme";

const CANVAS_HEIGHT = 250;

type ChipStatus = "ok" | "warn" | "bad";

/** RiggingTable palette convention: ok = primary, warn = amber, bad = warn red. */
function chipColor(status: ChipStatus, palette: HighliftPalette): string {
  return status === "bad" ? palette.warn : status === "warn" ? palette.amber : palette.primary;
}

/** Percent-of-chord formatting for the chord-normalized slot metrics (adaptive precision). */
function pctC(v: number): string {
  if (!Number.isFinite(v)) return "-";
  const pct = v * 100;
  return `${pct.toFixed(Math.abs(pct) >= 10 ? 1 : 2)}%c`;
}

interface SlotChip {
  label: string;
  value: string;
  status: ChipStatus;
  title: string;
}

/** Classify the slot metrics into ok / warn / bad chips. csSlotHealth already returns
 *  CHORD-NORMALIZED SlotMetrics (gap / overlap / exitWidth in chords), so the values are
 *  consumed as chord fractions directly and rendered as percent-of-chord. */
function slotChips(metrics: SlotMetrics): SlotChip[] {
  const gapStatus: ChipStatus = metrics.gap <= 1e-5 ? "bad" : metrics.gap < 0.002 ? "warn" : "ok";
  const exitStatus: ChipStatus =
    metrics.exitWidth <= 1e-5 ? "bad" : metrics.exitWidth < 0.002 ? "warn" : "ok";
  // <1 converging (accelerating slot, the design intent); >1 diffusing (separation-prone).
  const convStatus: ChipStatus =
    metrics.convergence <= 0 ? "warn" : metrics.convergence <= 1 ? "ok" : metrics.convergence <= 1.3 ? "warn" : "bad";
  return [
    {
      label: "gap",
      value: pctC(metrics.gap),
      status: gapStatus,
      title: "Slot throat width, percent of local chord",
    },
    {
      label: "overlap",
      value: pctC(metrics.overlap),
      status: "ok",
      title: "Chordwise overlap, percent of local chord - positive = overlapping (Fowler-like)",
    },
    {
      label: "exit",
      value: pctC(metrics.exitWidth),
      status: exitStatus,
      title: "Gap-exit width at the slot mouth, percent of local chord",
    },
    {
      label: "conv",
      value: metrics.convergence.toFixed(2),
      status: convStatus,
      title: "Slot convergence ratio = exit / gap - below 1 converging (good), above 1 diffusing",
    },
  ];
}

/** Extents of every drawn contour, for fit-to-view. */
function shapeExtents(shape: CsShapeResult): { x0: number; x1: number; z0: number; z1: number } {
  let x0 = Infinity;
  let x1 = -Infinity;
  let z0 = Infinity;
  let z1 = -Infinity;
  const scan = (xs: number[], zs: number[]) => {
    for (let i = 0; i < xs.length; i++) {
      const x = xs[i] ?? NaN;
      const z = zs[i] ?? NaN;
      if (!Number.isFinite(x) || !Number.isFinite(z)) continue;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (z < z0) z0 = z;
      if (z > z1) z1 = z;
    }
  };
  scan(shape.wing_contour.x, shape.wing_contour.z);
  scan(shape.cs_contour_defl.x, shape.cs_contour_defl.z);
  scan(shape.cs_contour_0.x, shape.cs_contour_0.z);
  return { x0, x1, z0, z1 };
}

export interface ControlSurfaceSectionPanelProps {
  palette: HighliftPalette;
  /** Engine result at the scrubber station, or null when the band leaves the airfoil. */
  shape: CsShapeResult | null;
  /** True while the sections behind the engine are still loading. */
  loading: boolean;
  /** Scrubber position within the CS band, 0..1. */
  sectionEta: number;
  onSectionEtaChange: (v: number) => void;
  /** Absolute eta the scrubber resolves to (for the readout). */
  absoluteEta: number | null;
  deflectionDeg: number;
  /**
   * Aircraft z-orientation from the segmenter (+1 z-up, -1 z-down). DISPLAY-ONLY: when -1 the
   * drawn vertical axis is mirrored (a canvas transform) so the aerodynamic UPPER surface reads
   * on top — the shape, slot chips and engine coords stay in the MODEL frame untouched.
   */
  upSign?: 1 | -1;
}

export function ControlSurfaceSectionPanel({
  palette,
  shape,
  loading,
  sectionEta,
  onSectionEtaChange,
  absoluteEta,
  deflectionDeg,
  upSign = 1,
}: ControlSurfaceSectionPanelProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const health = useMemo(() => (shape ? csSlotHealth(shape) : null), [shape]);
  const chips = useMemo(() => (health ? slotChips(health) : null), [health]);

  // Draw the section. Re-runs on shape/palette change and on container resize.
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const draw = () => {
      const cssWidth = wrap.clientWidth || 320;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(cssWidth * dpr);
      canvas.height = Math.round(CANVAS_HEIGHT * dpr);
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${CANVAS_HEIGHT}px`;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssWidth, CANVAS_HEIGHT);
      if (!shape) return;

      const { x0, x1, z0, z1 } = shapeExtents(shape);
      const spanX = Math.max(x1 - x0, 1e-9);
      const spanZ = Math.max(z1 - z0, 1e-9);
      const pad = 16;
      const scale = Math.min((cssWidth - 2 * pad) / spanX, (CANVAS_HEIGHT - 2 * pad) / spanZ);
      const offX = (cssWidth - spanX * scale) / 2;
      const offZ = (CANVAS_HEIGHT - spanZ * scale) / 2;
      // z up in section coords -> screen y down. For a z-DOWN aircraft (upSign -1) the drawn
      // vertical axis is MIRRORED so the aerodynamic upper surface reads on top — a pure canvas
      // transform (tz + the flipped z-domain); the shape coords stay in the model frame.
      const tz = (z: number) => (upSign === -1 ? -z : z);
      const zLo = upSign === -1 ? -z1 : z0;
      const px = (x: number) => offX + (x - x0) * scale;
      const pz = (z: number) => CANVAS_HEIGHT - (offZ + (tz(z) - zLo) * scale);

      const stroke = (xs: number[], zs: number[], color: string, width: number, dash: number[] = []) => {
        if (xs.length < 2) return;
        ctx.beginPath();
        ctx.setLineDash(dash);
        let started = false;
        for (let i = 0; i < xs.length; i++) {
          const x = xs[i] ?? NaN;
          const z = zs[i] ?? NaN;
          if (!Number.isFinite(x) || !Number.isFinite(z)) continue;
          if (!started) {
            ctx.moveTo(px(x), pz(z));
            started = true;
          } else {
            ctx.lineTo(px(x), pz(z));
          }
        }
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        ctx.stroke();
        ctx.setLineDash([]);
      };

      // Undeflected CS contour as a faint reference, then wing (primary) and
      // the DEFLECTED CS (amber) — the engine has already rotated it.
      stroke(shape.cs_contour_0.x, shape.cs_contour_0.z, palette.ink4, 1, [4, 4]);
      stroke(shape.wing_contour.x, shape.wing_contour.z, palette.primary, 1.8);
      stroke(shape.cs_contour_defl.x, shape.cs_contour_defl.z, palette.amber, 1.8);

      // Hinge dot.
      ctx.beginPath();
      ctx.arc(px(shape.hinge_x), pz(shape.hinge_z), 3.5, 0, 2 * Math.PI);
      ctx.fillStyle = palette.flaperonHinge;
      ctx.fill();
    };

    draw();
    const observer = new ResizeObserver(draw);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [palette, shape, upSign]);

  const mono: CSSProperties = { fontFamily: "var(--v5-font-mono, 'JetBrains Mono', monospace)" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div
        ref={wrapRef}
        style={{
          position: "relative",
          border: `1px solid ${palette.line}`,
          borderRadius: 7,
          background: palette.surface,
          overflow: "hidden",
        }}
      >
        <canvas ref={canvasRef} aria-label="Live 2D control-surface section" style={{ display: "block" }} />
        {loading ? (
          <div style={overlayStyle(palette)} aria-busy="true">
            Extracting sections...
          </div>
        ) : !shape ? (
          <div style={overlayStyle(palette)}>
            No section here - the hinge falls outside the airfoil at this station. Pull the hinge
            x/c or the span band back onto the surface.
          </div>
        ) : (
          <div
            style={{
              position: "absolute",
              top: 6,
              left: 8,
              display: "flex",
              gap: 10,
              fontSize: 10,
              ...mono,
              color: palette.ink3,
              pointerEvents: "none",
            }}
          >
            <span style={{ color: palette.primary }}>wing</span>
            <span style={{ color: palette.amber }}>cs @ {deflectionDeg.toFixed(0)} deg</span>
            <span style={{ color: palette.flaperonHinge }}>hinge</span>
          </div>
        )}
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: palette.ink3 }}>
        <span style={{ whiteSpace: "nowrap" }}>station</span>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={sectionEta}
          onChange={(e) => onSectionEtaChange(Number(e.target.value))}
          style={{ flex: 1 }}
          aria-label="Section station within the control-surface band"
        />
        <span style={{ ...mono, color: palette.ink2, minWidth: 74, textAlign: "right" }}>
          {absoluteEta != null ? `eta ${absoluteEta.toFixed(3)}` : "eta -"}
        </span>
      </label>

      {shape ? (
        chips ? (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} aria-label="Slot health">
            {chips.map((chip) => (
              <span
                key={chip.label}
                title={chip.title}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "2px 8px",
                  borderRadius: 999,
                  border: `1px solid ${palette.line}`,
                  background: palette.surface2,
                  fontSize: 10.5,
                  ...mono,
                  color: palette.ink2,
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    flex: "0 0 auto",
                    background: chipColor(chip.status, palette),
                  }}
                />
                {chip.label} {chip.value}
              </span>
            ))}
          </div>
        ) : (
          <span style={{ fontSize: 10.5, color: palette.ink4 }}>
            Slot health unavailable for this station - the cove contours are degenerate here.
          </span>
        )
      ) : null}
    </div>
  );
}

function overlayStyle(palette: HighliftPalette): CSSProperties {
  return {
    position: "absolute",
    inset: 0,
    display: "grid",
    placeItems: "center",
    padding: 18,
    textAlign: "center",
    fontSize: 12,
    lineHeight: 1.5,
    color: palette.ink3,
    background: "transparent",
  };
}
